import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getAccommodationsBlurPlaceholder } from "@/utils/accommodationsBlurPlaceholders";
import { Room } from "@/types/room";
import {
  Users,
  Square,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Wifi,
  Tv,
  Wind,
  ChefHat,
  Shield,
  Star,
  Share2,
  Coffee,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useViewportAutoplay } from "@/hooks/useViewportAutoplay";

interface AlpineDoubleTwinSectionProps {
  room: Room;
  onViewDetails?: (roomId: string) => void;
  onQuickBook?: (roomId: string) => void;
}

const AlpineDoubleTwinSection: React.FC<AlpineDoubleTwinSectionProps> = ({
  room,
  onViewDetails,
  onQuickBook,
}) => {
  const { t } = useLanguage();

  // Viewport-based auto-play hook
  const {
    currentImageIndex,
    isAutoPlaying,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick,
  } = useViewportAutoplay({
    imageCount: room.images.length,
    intervalMs: 4000,
    threshold: 0.7,
  });

  const handleViewDetails = () => {
    onViewDetails?.(room.id);
  };

  const handleQuickBook = () => {
    onQuickBook?.(room.id);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/accommodations#alpine-double-twin`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} - Park Hotel Beatenberg`,
          text: `Check out our Alpine Double/Twin Rooms with flexible bedding and stunning lake views`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard if share is cancelled
        copyToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const currentImage = room.images[currentImageIndex];

  // Feature badges data
  const featureBadges = [
    {
      icon: Users,
      label: "Occupancy",
      value: t("rooms.alpineDoubleTwinBadges.occupancy"),
      description: `${room.capacity.adults} adults, ${room.capacity.children} children`,
    },
    {
      icon: Square,
      label: "Room Size",
      value: t("rooms.alpineDoubleTwinBadges.roomSize"),
      description: `${room.size.squareFeet} sq ft`,
    },
    {
      icon: Bed,
      label: "Bedding",
      value: t("rooms.alpineDoubleTwinBadges.bedding"),
      description: "Flexible double or twin beds",
    },
    {
      icon: Bath,
      label: "Bathrooms",
      value: t("rooms.alpineDoubleTwinBadges.bathrooms"),
      description: "Ensuite bathroom",
    },
  ];

  return (
    <section
      id="alpine-double-twin"
      className="py-16 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden"
      data-oid="bldcirl"
    >
      {/* Background Quadrilaterals - Mirrored positions */}
      <div
        className="absolute top-0 right-0 w-96 h-96 transform rotate-45 translate-x-48 -translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="wxlq78u"
      ></div>
      <div
        className="absolute bottom-0 left-0 w-96 h-96 transform rotate-45 -translate-x-48 translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="ugv1m6d"
      ></div>
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        data-oid="e0ooe4g"
      >
        {/* Mobile Header - Shown only on mobile */}
        <div className="mb-8 lg:hidden" data-oid="bn.zf:7">
          <div className="mb-4" data-oid="ee_7an.">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-playfair text-gray-900"
              data-oid="07m2ana"
            >
              {t("rooms.alpineDoubleTwin")}
            </h2>
          </div>

          {/* Key badges below title - Mobile */}
          <div
            className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap"
            data-oid=":2umv_q"
          >
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="rou.xar"
            >
              <Users
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="vglr:bq"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="is0gwg-"
              >
                {featureBadges[0].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="a2ymwp1"
            >
              <Square
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="k.sgs4h"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid=":jusac."
              >
                {featureBadges[1].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="ib_25s1"
            >
              <Bed
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="vinfblx"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="zvvd3et"
              >
                {featureBadges[2].value}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout: Carousel Left (60%), Content Right (40%) - MIRRORED */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[60%_40%] xl:grid-cols-[55%_45%] 2xl:grid-cols-[60%_40%] gap-6 lg:gap-8 xl:gap-12 mb-12"
          data-oid="50vhznr"
        >
          {/* Left Column - Image Carousel */}
          <div
            ref={elementRef}
            className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl order-1 lg:order-1"
            data-oid="owi6_6_"
          >
            <div
              className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              data-oid="lcdevvm"
            >
              {room.images.map((image, index) => (
                <div
                  key={image.id}
                  className="w-full h-full flex-shrink-0"
                  data-oid="rk5r86w"
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    priority={false}
                    rootMargin="200px"
                    blurDataURL={getAccommodationsBlurPlaceholder(image.url)}
                    responsive={true}
                    data-oid="6goo7z9"
                  />
                </div>
              ))}
            </div>

            {/* Image Navigation */}
            {room.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  data-oid="ivr2uz_"
                >
                  <ChevronLeft className="w-5 h-5" data-oid="v1mgibt" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  data-oid="jx1:2-."
                >
                  <ChevronRight className="w-5 h-5" data-oid="auaibzt" />
                </Button>
              </>
            )}

            {/* Image Indicators */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
                data-oid="w12l88_"
              >
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    onClick={() => handleImageClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    data-oid="caxpthh"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Content */}
          <div
            className="flex flex-col gap-8 min-w-0 overflow-hidden pr-4 lg:pr-6 xl:pr-8 order-2 lg:order-2"
            data-oid="orbuw.e"
          >
            {/* Desktop Header - Shown only on desktop */}
            <div className="hidden lg:block" data-oid="su27dyu">
              <div className="mb-4" data-oid="s_0eb0u">
                <h2
                  className="text-3xl xl:text-4xl 2xl:text-5xl font-playfair text-gray-900"
                  data-oid="rjlsywg"
                >
                  {t("rooms.alpineDoubleTwin")}
                </h2>
              </div>

              {/* Key badges below title - Desktop */}
              <div
                className="flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-wrap"
                data-oid="f..nwh2"
              >
                <div className="flex items-center gap-2" data-oid="8pel_-6">
                  <Users
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="5__36cx"
                  />

                  <span className="text-sm text-gray-900" data-oid="my73.w-">
                    {featureBadges[0].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="kclb9ab">
                  <Square
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="qluv.sp"
                  />

                  <span className="text-sm text-gray-900" data-oid="l7x5i.7">
                    {featureBadges[1].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="0t.b6fb">
                  <Bed
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="3zhxo38"
                  />

                  <span className="text-sm text-gray-900" data-oid="me1cljg">
                    {featureBadges[2].value}
                  </span>
                </div>
              </div>
            </div>

            {/* 1. Description */}
            <div className="overflow-hidden" data-oid="mdhjvj9">
              <p
                className="text-sm lg:text-base xl:text-lg text-gray-700 leading-relaxed break-words"
                data-oid="8s8bw7g"
              >
                <span className="sm:hidden" data-oid="k-4q56k">
                  {t("rooms.alpineDoubleTwinDescription.mobile")}
                </span>
                <span className="hidden sm:inline" data-oid="g_o4lb_">
                  {t("rooms.alpineDoubleTwinDescription.desktop")}
                </span>
              </p>
            </div>

            {/* 2. All Features & Amenities - Consolidated */}
            <div className="grid grid-cols-1 gap-y-4" data-oid="y91hq4f">
              {/* Flexible bedding configuration */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="qo8klpg"
              >
                <Bed
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="mdas_ih"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="vhbxk.o"
                >
                  {t("rooms.alpineDoubleTwinFeatures.flexibleBedding")}
                </span>
              </div>

              {/* Private balcony with stunning lake views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="ls3q.fw"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="637q4wm"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="52ra-sa"
                >
                  {t("rooms.alpineDoubleTwinFeatures.privateBalconyWithViews")}
                </span>
              </div>

              {/* Free Wifi */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="dd3w.3u"
              >
                <Wifi
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="r.1j0ni"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="2k0nz5c"
                >
                  Free Wifi
                </span>
              </div>

              {/* Premium organic bio cotton bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="im12ngm"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="tph8-78"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="z_h746t"
                >
                  Premium organic bio cotton bedding
                </span>
              </div>

              {/* Ensuite coffee machine */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="mhxww2l"
              >
                <Coffee
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="pyi4yjd"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="539gv0r"
                >
                  Ensuite coffee machine
                </span>
              </div>
            </div>

            {/* 3. Book Now and Share Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              data-oid="evk46ms"
            >
              <Button
                asChild
                size="lg"
                className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Alpine Double Twin Section"
                data-oid="-vy.:0v"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  data-oid="j0pcidv"
                >
                  {t("nav.bookNow")}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-white/80 hover:bg-alpine-gold hover:text-white border-gray-300 hover:border-alpine-gold transition-all duration-300 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105"
                onClick={handleShare}
                aria-label="Share Alpine Double/Twin Rooms"
                data-oid="9nj8hkp"
              >
                <Share2 className="w-4 h-4 mr-2" data-oid="jsvcsu4" />
                {t("common.share")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AlpineDoubleTwinSection;
